<template lang="pug">
.activity-page.flex.column.gap-20
  .search-bar.flex.align-center.justify-space-between
    .filter.flex.align-end.gap-10
      .column.flex.column.align-start.gap-4
        .title 預約時間
        el-date-picker.date-range-input(
          @change="changeDate"
          v-model="times"
          type="daterange"
          range-separator="至")
    el-button(
      type="primary", icon="el-icon-plus"
      @click="$refs.activityEditor.$emit('show')"
      ) 新增活動
  .active-filters.flex.align-center.wrap.gap-6(v-if="activeFilters.length > 0")
    span.label 目前篩選條件：
    el-tag(
      v-for="f in activeFilters"
      :key="f.key"
      size="small"
      closable
      disable-transitions
      @close="removeFilter(f.key)") {{ f.label }}：{{ f.text }}
    el-button.clear-all(type="text", @click="clearAllFilters") 清除全部
  .table.flex
    el-table(:key="tableKey", :data="activities", empty-text="此篩選無任何活動記錄")
      el-table-column(label="活動名稱", prop="name")
        template(slot="header")
          .table-filter.flex.align-center.gap-4
            span 活動名稱
            el-popover(
              placement="bottom"
              trigger="click"
              width="220"
              popper-class="activity-filter-popover")
              .filter-content.flex.column.gap-10
                //- 表頭內容不會跟著父層 data 重繪，用原生 input 自行維護顯示值
                .el-input.el-input--mini
                  input.el-input__inner(
                    ref="nameFilterInput"
                    type="text"
                    placeholder="輸入活動名稱"
                    @input="nameInput = $event.target.value"
                    @keydown="submitOnEnter($event, 'name')")
                .actions.flex.align-center.justify-end.gap-6
                  el-button(size="mini", @click="clearFilter('name')") 清除
                  el-button(size="mini", type="primary", @click="applyFilter('name')") 篩選
              i.el-icon-search.clickable(
                slot="reference"
                :class="{ 'filter-active': !!nameFilter }")
        template(slot-scope="scope")
          .flex.align-center.justify-space-between
            .name {{ scope.row.name }}
            .note(v-if="scope.row.note")
              el-tooltip(:content="scope.row.note", placement="right")
                i.el-icon-chat-dot-round.clickable
      el-table-column(label="單位", prop="organization", width="180")
        template(slot="header")
          .table-filter.flex.align-center.gap-4
            span 單位
            el-popover(
              placement="bottom"
              trigger="click"
              width="220"
              popper-class="activity-filter-popover")
              .filter-content.flex.column.gap-10
                .el-input.el-input--mini
                  input.el-input__inner(
                    ref="organizationFilterInput"
                    type="text"
                    placeholder="輸入單位名稱"
                    @input="organizationInput = $event.target.value"
                    @keydown="submitOnEnter($event, 'organization')")
                .actions.flex.align-center.justify-end.gap-6
                  el-button(size="mini", @click="clearFilter('organization')") 清除
                  el-button(size="mini", type="primary", @click="applyFilter('organization')") 篩選
              i.el-icon-search.clickable(
                slot="reference"
                :class="{ 'filter-active': !!organizationFilter }")
      el-table-column(label="類型", width="150")
        template(slot="header")
          .table-filter.flex.align-center.gap-4
            span 類型
            el-popover(
              placement="bottom"
              trigger="click"
              width="240"
              popper-class="activity-filter-popover")
              .filter-content.flex.column.gap-10
                el-select(
                  size="mini"
                  clearable
                  :value="typeFilter"
                  @change="applySelectFilter('type', $event)"
                  placeholder="選擇活動類型")
                  el-option-group(
                    v-for="g in activityTypeGroups"
                    :key="g.label"
                    :label="g.label")
                    el-option(
                      v-for="opt in g.options"
                      :key="opt.value"
                      :label="opt.label"
                      :value="opt.value")
              i.el-icon-search.clickable(
                slot="reference"
                :class="{ 'filter-active': !!typeFilter }")
        template(slot-scope="scope")
          span(v-if="scope.row.activity_type") {{ $msg(`activity_type.${scope.row.activity_type}`) }}
          span(v-else) -
      el-table-column(label="活動建立人", width="130")
        template(slot="header")
          .table-filter.flex.align-center.gap-4
            span 活動建立人
            el-popover(
              v-if="creatorOptions.length > 0"
              placement="bottom"
              trigger="click"
              width="240"
              popper-class="activity-filter-popover")
              .filter-content.flex.column.gap-10
                el-select(
                  size="mini"
                  filterable
                  clearable
                  :value="creatorFilter"
                  @change="applySelectFilter('creator', $event)"
                  placeholder="選擇活動建立人")
                  el-option(
                    v-for="c in creatorOptions"
                    :key="c.id"
                    :label="c.name"
                    :value="c.id")
              i.el-icon-search.clickable(
                slot="reference"
                :class="{ 'filter-active': !!creatorFilter }")
        template(slot-scope="scope")
          span {{ scope.row.creator?.family_name || '' }}{{ scope.row.creator?.given_name || '' }}
      el-table-column(label="專業人士", width="120")
        template(slot="header", slot-scope="scope")
          .flex.align-center
            template(v-if="proSearch")
              pro-picker(
                size="mini"
                ref="proPicker"
                @clear="setProFilter(undefined)"
                @input="setProFilter")
            template(v-else)
              span 專業人士
              i.el-icon-search.clickable(@click="editProSearch")
        template(slot-scope="scope")
          pro-badge(
            :class="{'cancel-text': scope.row.isCancelled}"
            :professional="scope.row.professional")
      el-table-column(label="日期", width="100")
        template(slot-scope="scope")
          span {{ $timestampToDate(scope.row.start_at) }}
      el-table-column(label="時間", width="110")
        template(slot-scope="scope")
          .flex.align-center.gap-6
            span {{ $timestampToTime(scope.row.start_at) }}
            span -
            span {{ $timestampToTime(scope.row.end_at) }}
      el-table-column(label="預約空間", width="120")
        template(slot-scope="scope")
          template(v-if="scope.row.location_type === 'otherplace'")
            span {{ scope.row.showAddress}}
          template(v-else)
            span {{ scope.row.room?.branch?.name }}
            span &nbsp;{{ scope.row.room?.name }}
      el-table-column(label="鐘點統計", width="120")
        template(slot-scope="scope")
          span NT $ {{ scope.row.price }}
      el-table-column(label="分帳模式", width="120")
        template(slot-scope="scope")
          span {{ scope.row.price_model?.name || '-' }}
      el-table-column(label="操作" width="120")
        template(slot-scope="scope")
          .buttons.flex.align-center.gap-4
            el-button(icon="el-icon-edit-outline", size="mini", @click="popEditActivity(scope.row)")
            el-popconfirm(
              title="確定要取消活動嗎?"
              @confirm="deleteActivity(scope.row)")
              el-button(
                slot="reference"
                icon="el-icon-delete", size="mini", type="danger")
  .pagination.flex.align-center.justify-end(v-if="activities.length > 0")
    el-pagination(
      layout="total, sizes, prev, pager, next, ->, jumper"
      :page-size.sync="limit"
      :current-page.sync="currentPage"
      :total="total"
      :page-sizes="[50, 100, 200]"
    )
  activity-editor(
    ref="activityEditor"
    @finish="loadData"
  )
</template>

<script>
import { mapGetters } from 'vuex';
import { getActivities, deleteActivity } from '@/api/activity';
import { getAccounts } from '@/api/management';
import { getActivityTypeGroups } from '@/util/activity';
import ActivityEditorVue from '@/components/drawers/ActivityEditor.vue';

export default {
  components: {
    'activity-editor': ActivityEditorVue,
  },
  data() {
    const now = new Date();

    return {
      times: [],
      activities: [],

      total: 0,
      currentPage: 1,
      limit: 100,
      editBoundary: parseInt(now.getTime() / 1000, 10) - 86400,

      proSearch: false,
      proFilter: undefined,
      proFilterName: '',

      activityTypeGroups: getActivityTypeGroups(),
      accounts: [],

      // el-table 的表頭只在表格重繪時才會更新，套用/清除篩選時 bump 這個 key
      // 讓表頭跟著重繪，篩選器才會顯示目前套用的值
      tableKey: 0,

      // 表頭篩選器：xxxInput 為輸入中的值，xxxFilter 為已套用到 API 的值
      nameInput: '',
      nameFilter: '',

      organizationInput: '',
      organizationFilter: '',

      typeFilter: undefined,
      creatorFilter: undefined,
    };
  },
  computed: {
    ...mapGetters(['isAdmin', 'isAdvStaff', 'isAccounting']),
    creatorOptions() {
      return this.accounts.map((a) => ({
        id: a.id,
        name: a.enabled ? a.name : `${a.name}（停用）`,
      }));
    },
    activeFilters() {
      const filters = [];
      if (this.nameFilter) {
        filters.push({ key: 'name', label: '活動名稱', text: this.nameFilter });
      }
      if (this.organizationFilter) {
        filters.push({ key: 'organization', label: '單位', text: this.organizationFilter });
      }
      if (this.typeFilter) {
        filters.push({
          key: 'type',
          label: '類型',
          text: this.$msg(`activity_type.${this.typeFilter}`),
        });
      }
      if (this.creatorFilter) {
        const creator = this.creatorOptions.find((c) => c.id === this.creatorFilter);
        filters.push({
          key: 'creator',
          label: '活動建立人',
          text: creator ? creator.name : this.creatorFilter,
        });
      }
      if (this.proFilter) {
        filters.push({
          key: 'pro',
          label: '專業人士',
          text: this.proFilterName || this.proFilter,
        });
      }
      return filters;
    },
  },
  watch: {
    currentPage() {
      this.loadData();
    },
    limit() {
      this.reloadFromFirstPage();
    },
  },
  methods: {
    deleteActivity(activity) {
      this.$execWithLoading(async () => {
        await deleteActivity(activity.id);
        this.$showSuccess('取消活動成功');
        await this.loadActivities();
      }, (e) => {
        this.$showAxiosException('取消活動失敗', e);
      });
    },
    popEditActivity(activity) {
      this.$refs.activityEditor.$emit('show', activity);
    },
    editProSearch() {
      this.proSearch = true;
      this.$nextTick(() => {
        this.$refs.proPicker.$emit('focus');
      });
    },
    setProFilter(p) {
      if (p === undefined) {
        this.proSearch = false;
        this.proFilter = undefined;
        this.proFilterName = '';
      } else {
        this.proFilter = p.id;
        this.proFilterName = `${p.family_name || ''}${p.given_name || ''}`;
      }
      this.refreshTableHeader();
      this.reloadFromFirstPage();
    },
    // 中文選字時按 Enter 是在確認候選字（keydown 階段 isComposing 為 true、
    // keyCode 為 229），這個 Enter 不能當成送出
    submitOnEnter(event, key) {
      if (event.key !== 'Enter' || event.isComposing || event.keyCode === 229) {
        return;
      }
      this.applyFilter(key);
    },
    // 文字類篩選（活動名稱、單位）
    applyFilter(key) {
      this[`${key}Filter`] = (this[`${key}Input`] || '').trim();
      this.refreshTableHeader();
      this.reloadFromFirstPage();
    },
    clearFilter(key) {
      this[`${key}Input`] = '';
      this.applyFilter(key);
    },
    // 下拉類篩選（類型、活動建立人），選完即套用
    applySelectFilter(key, value) {
      this[`${key}Filter`] = value || undefined;
      this.refreshTableHeader();
      this.reloadFromFirstPage();
    },
    removeFilter(key) {
      if (key === 'pro') {
        this.setProFilter(undefined);
        return;
      }
      if (key === 'name' || key === 'organization') {
        this[`${key}Input`] = '';
        this[`${key}Filter`] = '';
      } else {
        this[`${key}Filter`] = undefined;
      }
      this.refreshTableHeader();
      this.reloadFromFirstPage();
    },
    clearAllFilters() {
      this.nameInput = '';
      this.nameFilter = '';
      this.organizationInput = '';
      this.organizationFilter = '';
      this.typeFilter = undefined;
      this.creatorFilter = undefined;
      this.proSearch = false;
      this.proFilter = undefined;
      this.proFilterName = '';
      this.refreshTableHeader();
      this.reloadFromFirstPage();
    },
    refreshTableHeader() {
      this.nameInput = this.nameFilter;
      this.organizationInput = this.organizationFilter;
      this.tableKey += 1;
      // 表頭是整個重新建立的，等 render 完再把已套用的值寫回控制項
      this.$nextTick(() => {
        this.syncHeaderInputs();
      });
    },
    syncHeaderInputs() {
      const nameInputEl = this.$refs.nameFilterInput;
      if (nameInputEl) {
        nameInputEl.value = this.nameFilter || '';
      }
      const organizationInputEl = this.$refs.organizationFilterInput;
      if (organizationInputEl) {
        organizationInputEl.value = this.organizationFilter || '';
      }
      if (this.proFilterName && this.$refs.proPicker) {
        this.$refs.proPicker.$emit('name', this.proFilterName);
      }
    },
    reloadFromFirstPage() {
      if (this.currentPage !== 1) {
        // currentPage 的 watcher 會接著觸發 loadData
        this.currentPage = 1;
        return;
      }
      this.loadData();
    },
    async loadAccounts() {
      try {
        const rsp = await getAccounts();
        this.accounts = [...rsp].sort((a, b) => {
          if (a.enabled !== b.enabled) {
            return a.enabled ? -1 : 1;
          }
          return `${a.name}`.localeCompare(`${b.name}`, 'zh-Hant');
        });
      } catch (e) {
        // 帳務人員無權讀取後台帳號清單，此時不提供活動建立人篩選
        this.accounts = [];
      }
      // 帳號清單是非同步載入的，載完要讓表頭重繪才吃得到建立人選項
      this.refreshTableHeader();
    },
    execCommand() {
    },
    getFilter() {
      if (!this.times || this.times.length === 0) {
        this.times = this.getDefaultTime();
      }
      const [start, end] = this.times;
      end.setHours(23, 59, 59, 999);
      const filter = {
        start_at: parseInt(start.getTime() / 1000, 10),
        end_at: parseInt(end.getTime() / 1000, 10),
        page: this.currentPage - 1,
        limit: this.limit,
        professional_id: this.proFilter,
      };
      if (this.nameFilter) {
        filter.name = this.nameFilter;
      }
      if (this.organizationFilter) {
        filter.organization = this.organizationFilter;
      }
      if (this.typeFilter) {
        filter.activity_type = this.typeFilter;
      }
      if (this.creatorFilter) {
        filter.creator_id = this.creatorFilter;
      }
      return filter;
    },
    changeDate() {
      this.reloadFromFirstPage();
    },
    loadData() {
      this.$execWithLoading(async () => {
        await this.loadActivities();
      }, (e) => this.$showAxiosException('讀取活動清單失敗', e));
    },
    async loadActivities() {
      const rsp = await getActivities(this.getFilter());
      let activities = rsp;
      if (rsp.activities) {
        activities = rsp.activities;
        this.total = rsp.meta.total;
      }
      this.activities = activities.map((a) => {
        let description = '';
        let note = '';
        try {
          const data = JSON.parse(a.description);
          if (typeof data === typeof []) {
            description = data[0] || '';
            note = data[1] || '';
          }
        } catch (e) {
          description = a.description;
        }
        let showAddress = a.address || '';
        if (showAddress.indexOf(`${a.city}${a.district}`) < 0) {
          showAddress = `${a.city}${a.district}${a.address}`;
        }
        return {
          ...a,
          description,
          note,
          showAddress,
        };
      });
    },
    getDefaultTime() {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const start = new Date(now.getTime());
      const end = new Date(now.getTime() + 7 * 86400 * 1000);
      end.setHours(0, 0, 0, 0);
      return [start, end];
    },
  },
  mounted() {
    if (!this.isAdmin && !this.isAdvStaff && !this.isAccounting) {
      this.$router.push('/reserve');
      return;
    }
    this.times = this.getDefaultTime();
    this.loadAccounts();
    setTimeout(() => {
      this.loadData();
    }, 10);
  },
};
</script>

<style lang="scss" scoped>
.activity-page {
  position: relative;
  padding: 30px 0;
  .search-bar {
    padding: 0 30px;
    .filter {
      .column {
        .title {
          font-size: 12px;
        }
        .date-range-input {
          width: 440px;
        }
        .place-input {
          width: 300px;
        }
      }
    }
  }
  .active-filters {
    flex: 0 0 auto;
    padding: 0 30px;
    margin-top: -10px;
    .label {
      font-size: 12px;
    }
    .clear-all {
      padding: 0;
      font-size: 12px;
    }
  }
  .table {
    flex: 1;
  }
  .pagination {
    flex: 0 0 auto;
  }
}
</style>

<style lang="scss">
@import '@/assets/style/variables.scss';

.activity-page {
  & > .table {
    margin: 0 30px;
    overflow: hidden;
    .td-tags {
      .cell {
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
      }
    }
    .td-name {
      color: $primary-color;
    }
  }

  // hack
  .el-table {
    border: 1px solid #EBEEF5;
    border-bottom: none;
    border-left: none;
    display: flex;
    flex-direction: column;
    .el-table__header {
      thead {
        tr {
          th {
            background: #E6F4F4;
            border-left: 1px solid #EBEEF5;
          }
        }
      }
    }
    .el-table__body {
      tbody {
        tr {
          td {
            border-left: 1px solid #EBEEF5;
          }
        }
      }
    }

    // handle scroll
    .el-table__header-wrapper {
      flex: 0 0 auto;
    }
    .el-table__body-wrapper {
      flex: 1;
      overflow: overlay;
    }
  }
  .dropdown-button {
    padding: 7px;
  }
  .cancel-text {
    pointer-events: none;
    color: $text-placeholder-color;
  }
  .table-filter {
    .el-icon-search {
      font-size: 13px;
      &.filter-active {
        color: $primary-color;
      }
    }
  }
}

// 表頭篩選器的浮層（el-popover 預設 append 到 body，需寫在 scoped 之外）
.activity-filter-popover {
  padding: 12px;
  .filter-content {
    .el-input,
    .el-select {
      width: 100%;
    }
  }
}
</style>
